import React, { useState } from 'react';
import PropTypes from 'prop-types';

import { useTranslator } from 'tradera/lang/translate';
import useIsMobile from 'tradera/hooks/use-is-mobile';
import GdprSettings from 'tradera/utils/gdpr-settings';
import AppleLoginButton from './apple-login-button';
import KlarnaLoginButton from './klarna-login-button';
import ErrorIcon from '@tradera/blueprint/theme/icons2/alert.svg?jsx';
import FacebookLoginButton from './facebook-login-button';
import GoogleLoginButton from './google-login-button';
import InfoIcon from '@tradera/blueprint/theme/icons2/info.svg?jsx';
import { Divider } from 'tradera/components/divider/divider';
import cookieUtil from 'tradera/utils/cookie';
import { SHIPPING_COUNTRY } from 'tradera/constants/cookies';
import { isSwedenCountryCodeOrUndefined } from 'tradera/localization/countrycode';

const SocialLogins = ({
    appleClientId,
    facebookAppId,
    className,
    googleAppId,
    isModal,
    klarnaAppId,
    klarnaEnvironment
}) => {
    const cookiesEnabled = GdprSettings.isFunctionalEnabled();
    const isAnySocialLoginEnabled =
        appleClientId || facebookAppId || googleAppId || klarnaAppId;
    const [error, setError] = useState(null);
    const { t } = useTranslator();
    const isMobile = useIsMobile();

    if (!isAnySocialLoginEnabled) {
        return null;
    }

    const cookieValue = cookieUtil.readCookie(SHIPPING_COUNTRY);
    const showKlarnaLogin = isSwedenCountryCodeOrUndefined(cookieValue);

    return (
        <div className={className}>
            <div className="d-flex d-md-block justify-content-around py-1">
                {googleAppId && (
                    <GoogleLoginButton
                        googleAppId={googleAppId}
                        className="my-1"
                        compact={isMobile}
                        disabled={!cookiesEnabled}
                        isModal={isModal}
                        onError={(error) => setError(error)}
                    />
                )}
                {klarnaAppId && showKlarnaLogin && (
                    <KlarnaLoginButton
                        klarnaAppId={klarnaAppId}
                        klarnaEnvironment={klarnaEnvironment}
                        className="my-1"
                        compact={isMobile}
                        disabled={!cookiesEnabled}
                        isModal={isModal}
                        onError={(error) => setError(error)}
                        tabIndex={0}
                    />
                )}
                {facebookAppId && (
                    <FacebookLoginButton
                        className="my-1"
                        compact={isMobile}
                        disabled={!cookiesEnabled}
                        facebookAppId={facebookAppId}
                        isModal={isModal}
                        onError={(error) => setError(error)}
                        tabIndex={0}
                    />
                )}
                {appleClientId && (
                    <AppleLoginButton
                        appleClientId={appleClientId}
                        className="my-1"
                        compact={isMobile}
                        disabled={!cookiesEnabled}
                        isModal={isModal}
                        onError={(error) => setError(error)}
                        tabIndex={0}
                    />
                )}
            </div>
            {!cookiesEnabled && (
                <div className="d-flex align-items-start my-1 position-relative pl-2">
                    <InfoIcon
                        className="icon position-absolute"
                        style={{ left: 0 }}
                    />
                    <p className="m-0 ml-2 size-oslo text-inter-light text-left">
                        {t('login_cookieMissingWarning')}
                    </p>
                </div>
            )}
            {error && (
                <div className="alert alert-danger text-left my-1">
                    <p className="mb-0">
                        <ErrorIcon className="icon align-text-bottom" />
                        &nbsp;{t('login_error')}
                    </p>
                </div>
            )}

            <Divider role="presentation">
                <strong className="size-oslo text-styled">
                    {t('login_orEnter')}
                </strong>
            </Divider>
        </div>
    );
};

SocialLogins.propTypes = {
    appleClientId: PropTypes.string,
    facebookAppId: PropTypes.string,
    googleAppId: PropTypes.string,
    klarnaAppId: PropTypes.string,
    className: PropTypes.string,
    isModal: PropTypes.bool
};

SocialLogins.defaultProps = {
    className: ''
};

export default SocialLogins;
